import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Button from '@material-ui/core/Button';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import humps from 'humps';

import http from '../../shared/http';
import materialTheme from '../../reactMaterialTheme';

import './PublisherSelect.scss';

const useActivePublisherId = (CreativeService, RenderService) => {
  const [activePublisherId, setActivePublisherId] = useState(null);
  useEffect(() => {
    if (!activePublisherId) {
      return;
    }
    CreativeService.set('siteId', activePublisherId);
    // RenderService.setSite() relies on CreativeService.creative.siteId having
    // been updated by the call to CreativeService.set (sorry :/)
    RenderService.setSite(activePublisherId);
    RenderService.renderDemo();
  }, [activePublisherId]);

  return {
    activePublisherId,
    setActivePublisherId
  };
};

const PublisherSelect = props => {
  const [publishers, setPublishers] = useState([]);
  const [siteGroup, setSiteGroup] = useState('mobile');
  const { activePublisherId, setActivePublisherId } = useActivePublisherId(
    props.CreativeService,
    props.RenderService
  );

  useEffect(() => {
    http.get('/partners/sites').then(({ data }) => {
      const pubs = humps.camelizeKeys(data);
      setPublishers(pubs);

      const publisher = props.RenderService.site || pubs[0];
      if (publisher) {
        setActivePublisherId(publisher.id);
      }
    });
  }, []);

  useEffect(() => {
    props.RenderService.siteGroup = siteGroup;
    const newSite = sitesInGroup(siteGroup).filter(siteIsEnabled)[0];
    if (newSite) {
      setActivePublisherId(newSite.id);
      props.RenderService.site = newSite;
      props.RenderService.renderDemo();
    }
  }, [siteGroup]);

  const handleChange = event => {
    setActivePublisherId(event.target.value);
  };

  const sitesInGroup = group => publishers.filter(pub => pub.group === group);
  const { CreativeService } = props;
  const windowBigEnough = siteGroup === 'mobile' || window.innerWidth >= 768;

  const siteIsEnabled = (site) => {
    if (CreativeService.isCreativeInstreamVideo() !== site.instreamEnabled) {
      return false;
    }

    const isValidInstantPlay = CreativeService.instantPlaySupported(site);
    const isValidForIsi = !CreativeService.isCreativeIsi() || site.isiEnabled;
    const isValidForEnhanced = !CreativeService.isEnhancedAd() || site.enhancedAdsEnabled;
    const isValidForCaption = !CreativeService.isCreativeCaptioned() || site.captionsEnabled;

    // if any of these is false we want to disable the ad. the naming is a bit confusing,
    // but basically if the ad IS a video we check if it supports video, etc.
    return (
      windowBigEnough &&
      isValidInstantPlay &&
      isValidForIsi &&
      isValidForEnhanced &&
      isValidForCaption
    );
  };

  const getButtonClass = button => {
    const baseClass = 'group-select_button';

    if (button === siteGroup) {
      return `${baseClass} ${baseClass}--active`;
    }

    return baseClass;
  };

  if (!(activePublisherId && publishers.length > 0)) {
    return null;
  }

  return (
    <MuiThemeProvider theme={materialTheme}>
      <FormControl className="publisher-select">
        {props.showGroupSelect && (
          <div className="group-select">
            <Button
              onClick={() => setSiteGroup('mobile')}
              className={getButtonClass('mobile')}
            >
              Mobile
            </Button>
            <Button
              onClick={() => setSiteGroup('desktop')}
              className={getButtonClass('desktop')}
            >
              Desktop
            </Button>
          </div>
        )}
        <div className="publisher-select_select">
          <span className="publisher-select_select_label">Publisher</span>
          <Select
            value={activePublisherId}
            inputProps={{ id: 'publisher' }}
            onChange={handleChange}
            data-testid="publisher-select"
          >
            {sitesInGroup(siteGroup).map(publisher => (
              <MenuItem
                value={publisher.id}
                key={publisher.id}
                disabled={!siteIsEnabled(publisher)}
                classes={{ selected: 'publisher--selected' }}
              >
                {publisher.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </FormControl>
    </MuiThemeProvider>
  );
};

PublisherSelect.propTypes = {
  CreativeService: PropTypes.shape({
    set: PropTypes.func,
    instantPlaySupported: PropTypes.func,
    isCreativeIsi: PropTypes.func,
    isCreativeInstreamVideo: PropTypes.func,
    isEnhancedAd: PropTypes.func,
    isCreativeCaptioned: PropTypes.func
  }).isRequired,
  RenderService: PropTypes.shape({
    renderDemo: PropTypes.func,
    setSite: PropTypes.func,
    siteGroup: PropTypes.string,
    site: PropTypes.shape({
      group: PropTypes.string,
      name: PropTypes.string,
      id: PropTypes.number
    })
  }).isRequired,
  showGroupSelect: PropTypes.bool
};

PublisherSelect.defaultProps = {
  showGroupSelect: true
};

export default PublisherSelect;
